import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  Bot,
  Settings,
  ToggleLeft,
  ToggleRight,
  Users,
  MessageSquare,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Building2,
  Palette,
  Shield,
  Sparkles,
  Image,
  MessageCircle,
  Volume2,
  Ban,
  Ticket,
  Mail,
  Phone,
  ChevronDown,
  ChevronRight,
  Search,
  Plus,
  Trash2,
  Edit
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';

/**
 * AI Assistant Configuration Page
 * Configure the client-facing AI help desk
 * Phase 1: Full UI Shell with simulated data
 */

const AIAssistantPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [config, setConfig] = useState({
    enabled: true,
    assistant_name: 'FundElite AI',
    avatar_url: '',
    tone: 'professional',
    welcome_message: "Hi! I'm your FundElite AI assistant. How can I help you with your credit repair journey today?",
    daily_message_limit: 100,
    token_cap_per_session: 4000,
    restricted_topics: ['internal_logic', 'api_keys', 'legal_advice'],
    collect_email: true,
    collect_phone: false,
    auto_create_ticket: true,
    model_tier: 'mid',
    response_language: 'english',
    enable_voice: false,
    enable_images: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabledAgencies, setEnabledAgencies] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    global: true,
    appearance: true,
    limits: true,
    tickets: false,
    restricted: false,
    agencies: false
  });
  const [searchAgency, setSearchAgency] = useState('');
  const [customTopic, setCustomTopic] = useState('');

  // Mock agencies data
  const mockAgencies = [
    { id: '1', name: 'Premier Credit Repair', assistant_name: 'Premier AI', daily_message_limit: 150, enabled: true },
    { id: '2', name: 'Credit Solutions Inc', assistant_name: 'Solutions Bot', daily_message_limit: 100, enabled: true },
    { id: '3', name: 'Fast Track Credit', assistant_name: 'FastTrack AI', daily_message_limit: 75, enabled: false }
  ];

  useEffect(() => {
    fetchConfig();
    fetchEnabledAgencies();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch('/api/admin/ai/assistant/config', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnabledAgencies = async () => {
    try {
      const response = await fetch('/api/admin/ai/assistant/agencies', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setEnabledAgencies(data.agencies || mockAgencies);
      } else {
        setEnabledAgencies(mockAgencies);
      }
    } catch (error) {
      console.error('Failed to fetch enabled agencies:', error);
      setEnabledAgencies(mockAgencies);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/admin/ai/assistant/config', {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        toast.success('AI Assistant configuration saved');
      } else {
        // Simulate success for Phase 1
        toast.success('Configuration saved (Simulated)');
      }
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.success('Configuration saved (Simulated)');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const addRestrictedTopic = () => {
    if (customTopic.trim() && !config.restricted_topics.includes(customTopic.trim())) {
      setConfig(prev => ({
        ...prev,
        restricted_topics: [...prev.restricted_topics, customTopic.trim()]
      }));
      setCustomTopic('');
    }
  };

  const removeRestrictedTopic = (topic) => {
    setConfig(prev => ({
      ...prev,
      restricted_topics: prev.restricted_topics.filter(t => t !== topic)
    }));
  };

  const toneOptions = [
    { value: 'professional', label: 'Professional', description: 'Formal and business-like', icon: '👔' },
    { value: 'friendly', label: 'Friendly', description: 'Warm and approachable', icon: '😊' },
    { value: 'casual', label: 'Casual', description: 'Relaxed and conversational', icon: '👋' },
    { value: 'empathetic', label: 'Empathetic', description: 'Understanding and supportive', icon: '💚' }
  ];

  const modelTiers = [
    { value: 'small', label: 'Small (Cost-Efficient)', description: 'Best for simple queries', cost: '$0.001/1K tokens' },
    { value: 'mid', label: 'Mid (Balanced)', description: 'Good balance of quality and cost', cost: '$0.003/1K tokens' },
    { value: 'strong', label: 'Strong (High Quality)', description: 'Best responses, higher cost', cost: '$0.015/1K tokens' }
  ];

  const predefinedRestrictedTopics = [
    { value: 'internal_logic', label: 'Internal System Logic' },
    { value: 'api_keys', label: 'API Keys & Secrets' },
    { value: 'legal_advice', label: 'Legal Advice' },
    { value: 'pricing_changes', label: 'Pricing Changes' },
    { value: 'competitor_info', label: 'Competitor Information' },
    { value: 'refund_policy', label: 'Refund Policy Details' },
    { value: 'staff_info', label: 'Staff Personal Info' }
  ];

  const filteredAgencies = enabledAgencies.filter(agency =>
    agency.name?.toLowerCase().includes(searchAgency.toLowerCase())
  );

  const SectionHeader = ({ title, icon: Icon, section, badge }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 hover:bg-slate-800/30 transition-colors"
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-emerald-400" />
        <span className="font-medium text-white">{title}</span>
        {badge && (
          <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-xs">
            {badge}
          </Badge>
        )}
      </div>
      {expandedSections[section] ? (
        <ChevronDown className="w-4 h-4 text-slate-400" />
      ) : (
        <ChevronRight className="w-4 h-4 text-slate-400" />
      )}
    </button>
  );

  const ToggleSwitch = ({ enabled, onToggle, size = 'default' }) => (
    <button onClick={onToggle} className="focus:outline-none" data-testid="toggle-switch">
      {enabled ? (
        <ToggleRight className={`${size === 'large' ? 'w-12 h-12' : 'w-8 h-8'} text-emerald-400`} />
      ) : (
        <ToggleLeft className={`${size === 'large' ? 'w-12 h-12' : 'w-8 h-8'} text-slate-500`} />
      )}
    </button>
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Bot className="w-5 h-5 text-white" />
            </div>
            AI Assistant Configuration
          </h1>
          <p className="text-slate-400 mt-1">
            Configure the client-facing AI help desk for your agencies
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="save-ai-assistant-config"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Configuration
        </Button>
      </div>

      {/* Global Enable/Disable */}
      <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)] overflow-hidden">
        <SectionHeader title="Global Settings" icon={Settings} section="global" />
        {expandedSections.global && (
          <CardContent className="pt-0 pb-4 px-4 space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 border border-slate-700/50">
              <div>
                <p className="font-medium text-white">Enable AI Assistant (Global)</p>
                <p className="text-sm text-slate-400">Turn on the AI help desk system-wide</p>
              </div>
              <ToggleSwitch
                enabled={config.enabled}
                onToggle={() => setConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
                size="large"
              />
            </div>

            {config.enabled && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-emerald-300">
                    AI Assistant is active. Configure settings below.
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Appearance Settings */}
      <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)] overflow-hidden">
        <SectionHeader title="Appearance & Personality" icon={Palette} section="appearance" />
        {expandedSections.appearance && (
          <CardContent className="pt-0 pb-4 px-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Assistant Name</Label>
                <Input
                  value={config.assistant_name}
                  onChange={(e) => setConfig(prev => ({ ...prev, assistant_name: e.target.value }))}
                  placeholder="FundElite AI"
                  className="mt-1 bg-slate-800/50 border-slate-700 text-white"
                  data-testid="assistant-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Avatar URL (Optional)</Label>
                <Input
                  value={config.avatar_url}
                  onChange={(e) => setConfig(prev => ({ ...prev, avatar_url: e.target.value }))}
                  placeholder="https://..."
                  className="mt-1 bg-slate-800/50 border-slate-700 text-white"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300">Tone / Personality</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                {toneOptions.map((tone) => (
                  <button
                    key={tone.value}
                    onClick={() => setConfig(prev => ({ ...prev, tone: tone.value }))}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      config.tone === tone.value
                        ? 'border-emerald-500/50 bg-emerald-500/10'
                        : 'border-slate-700 hover:border-slate-600 bg-slate-800/30'
                    }`}
                    data-testid={`tone-${tone.value}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span>{tone.icon}</span>
                      <span className={`text-sm font-medium ${config.tone === tone.value ? 'text-emerald-300' : 'text-slate-300'}`}>
                        {tone.label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{tone.description}</p>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300">Welcome Message</Label>
              <Textarea
                value={config.welcome_message}
                onChange={(e) => setConfig(prev => ({ ...prev, welcome_message: e.target.value }))}
                placeholder="Hi! How can I help you today?"
                rows={3}
                className="mt-1 bg-slate-800/50 border-slate-700 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">This message appears when users first open the chat widget</p>
            </div>

            {/* Additional Features */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                <div className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-blue-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-300">Enable Voice</p>
                    <p className="text-xs text-slate-500">Text-to-speech responses</p>
                  </div>
                </div>
                <ToggleSwitch
                  enabled={config.enable_voice}
                  onToggle={() => setConfig(prev => ({ ...prev, enable_voice: !prev.enable_voice }))}
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                <div className="flex items-center gap-2">
                  <Image className="w-4 h-4 text-purple-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-300">Enable Images</p>
                    <p className="text-xs text-slate-500">Allow image attachments</p>
                  </div>
                </div>
                <ToggleSwitch
                  enabled={config.enable_images}
                  onToggle={() => setConfig(prev => ({ ...prev, enable_images: !prev.enable_images }))}
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Limits & Model Settings */}
      <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)] overflow-hidden">
        <SectionHeader title="Limits & Model" icon={Sparkles} section="limits" />
        {expandedSections.limits && (
          <CardContent className="pt-0 pb-4 px-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Daily Message Limit</Label>
                <Input
                  type="number"
                  value={config.daily_message_limit}
                  onChange={(e) => setConfig(prev => ({ ...prev, daily_message_limit: parseInt(e.target.value) || 0 }))}
                  className="mt-1 bg-slate-800/50 border-slate-700 text-white"
                />
                <p className="text-xs text-slate-500 mt-1">Max messages per user per day</p>
              </div>
              <div>
                <Label className="text-slate-300">Token Cap / Session</Label>
                <Input
                  type="number"
                  value={config.token_cap_per_session}
                  onChange={(e) => setConfig(prev => ({ ...prev, token_cap_per_session: parseInt(e.target.value) || 0 }))}
                  className="mt-1 bg-slate-800/50 border-slate-700 text-white"
                />
                <p className="text-xs text-slate-500 mt-1">Max tokens per conversation session</p>
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300">Model Tier</Label>
              <div className="space-y-2 mt-2">
                {modelTiers.map((tier) => (
                  <button
                    key={tier.value}
                    onClick={() => setConfig(prev => ({ ...prev, model_tier: tier.value }))}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      config.model_tier === tier.value
                        ? 'border-emerald-500/50 bg-emerald-500/10'
                        : 'border-slate-700 hover:border-slate-600 bg-slate-800/30'
                    }`}
                    data-testid={`model-tier-${tier.value}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-sm font-medium ${config.model_tier === tier.value ? 'text-emerald-300' : 'text-slate-300'}`}>
                          {tier.label}
                        </p>
                        <p className="text-xs text-slate-500">{tier.description}</p>
                      </div>
                      <Badge className="bg-slate-700 text-slate-300 border-0 text-xs">{tier.cost}</Badge>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Ticket Escalation Settings */}
      <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)] overflow-hidden">
        <SectionHeader title="Ticket Escalation" icon={Ticket} section="tickets" />
        {expandedSections.tickets && (
          <CardContent className="pt-0 pb-4 px-4 space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
              <div>
                <p className="text-sm font-medium text-slate-300">Auto-create support ticket</p>
                <p className="text-xs text-slate-500">When AI cannot resolve the issue</p>
              </div>
              <ToggleSwitch
                enabled={config.auto_create_ticket}
                onToggle={() => setConfig(prev => ({ ...prev, auto_create_ticket: !prev.auto_create_ticket }))}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-400" />
                <div>
                  <p className="text-sm font-medium text-slate-300">Collect email</p>
                  <p className="text-xs text-slate-500">Request email for ticket follow-up</p>
                </div>
              </div>
              <ToggleSwitch
                enabled={config.collect_email}
                onToggle={() => setConfig(prev => ({ ...prev, collect_email: !prev.collect_email }))}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-green-400" />
                <div>
                  <p className="text-sm font-medium text-slate-300">Collect phone</p>
                  <p className="text-xs text-slate-500">Request phone number for callback</p>
                </div>
              </div>
              <ToggleSwitch
                enabled={config.collect_phone}
                onToggle={() => setConfig(prev => ({ ...prev, collect_phone: !prev.collect_phone }))}
              />
            </div>

            {config.auto_create_ticket && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 mt-2">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
                  <p className="text-xs text-amber-300">
                    Tickets will be created in the AI Tickets queue when escalation triggers.
                    Make sure to monitor the queue regularly.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Restricted Topics */}
      <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)] overflow-hidden">
        <SectionHeader title="Restricted Topics" icon={Ban} section="restricted" badge={`${config.restricted_topics.length} active`} />
        {expandedSections.restricted && (
          <CardContent className="pt-0 pb-4 px-4 space-y-4">
            <p className="text-sm text-slate-400">
              Topics the AI will decline to discuss. Users will be redirected to human support.
            </p>
            
            {/* Predefined Topics */}
            <div className="space-y-2">
              {predefinedRestrictedTopics.map((topic) => {
                const isRestricted = config.restricted_topics.includes(topic.value);
                return (
                  <div key={topic.value} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <span className="text-sm text-slate-300">{topic.label}</span>
                    <ToggleSwitch
                      enabled={isRestricted}
                      onToggle={() => {
                        setConfig(prev => ({
                          ...prev,
                          restricted_topics: isRestricted
                            ? prev.restricted_topics.filter(t => t !== topic.value)
                            : [...prev.restricted_topics, topic.value]
                        }));
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Custom Topics */}
            <div className="pt-2 border-t border-slate-700/50">
              <Label className="text-slate-300">Add Custom Restricted Topic</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={customTopic}
                  onChange={(e) => setCustomTopic(e.target.value)}
                  placeholder="e.g., financial projections"
                  className="flex-1 bg-slate-800/50 border-slate-700 text-white"
                  onKeyPress={(e) => e.key === 'Enter' && addRestrictedTopic()}
                />
                <Button
                  onClick={addRestrictedTopic}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Custom Topics List */}
            {config.restricted_topics.filter(t => !predefinedRestrictedTopics.map(p => p.value).includes(t)).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {config.restricted_topics
                  .filter(t => !predefinedRestrictedTopics.map(p => p.value).includes(t))
                  .map((topic, idx) => (
                    <Badge
                      key={idx}
                      className="bg-red-500/20 text-red-300 border border-red-500/30 flex items-center gap-1"
                    >
                      {topic}
                      <button onClick={() => removeRestrictedTopic(topic)} className="ml-1 hover:text-white">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Agency Assignments */}
      <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)] overflow-hidden">
        <SectionHeader title="Agency Assignments" icon={Building2} section="agencies" badge={`${enabledAgencies.filter(a => a.enabled).length} enabled`} />
        {expandedSections.agencies && (
          <CardContent className="pt-0 pb-4 px-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={searchAgency}
                onChange={(e) => setSearchAgency(e.target.value)}
                placeholder="Search agencies..."
                className="pl-10 bg-slate-800/50 border-slate-700 text-white"
              />
            </div>

            {filteredAgencies.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-500">No agencies found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredAgencies.map((agency) => (
                  <div
                    key={agency.id}
                    className={`p-4 rounded-lg border transition-all ${
                      agency.enabled
                        ? 'border-emerald-500/30 bg-emerald-500/5'
                        : 'border-slate-700 bg-slate-800/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-white">{agency.name}</h4>
                          {agency.enabled && (
                            <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-xs">
                              Active
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-400 mt-1">
                          Bot: {agency.assistant_name} • Limit: {agency.daily_message_limit} msgs/day
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-400 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <ToggleSwitch
                          enabled={agency.enabled}
                          onToggle={() => {
                            setEnabledAgencies(prev =>
                              prev.map(a => a.id === agency.id ? { ...a, enabled: !a.enabled } : a)
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Security Notice */}
      <div className="p-4 rounded-lg bg-slate-800/30 border border-slate-700/50">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-slate-300">Security & Privacy</p>
            <p className="text-xs text-slate-500 mt-1">
              All AI conversations are logged for quality assurance. Sensitive topics are blocked.
              User data is handled in compliance with privacy regulations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantPage;
